import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu as MenuIcon, X, Search, Phone, Mail, MapPin, Clock, ChevronDown, Camera, Send, MessageCircle, Globe, ShoppingCart, Scale, Sun, Moon, Check } from 'lucide-react';
import { STORAGE_BASE } from '../config';
import { ICONS } from './WidgetIcons';
import CustomerAccount from '../components/CustomerAccount';
import { WidgetText, WidgetImage, WidgetDivider, WidgetHtml } from './WidgetRender';
import { widgetStyleObj } from '../components/widgetStyle';
export const resolveSrc = p => (!p ? '' : (/^https?:\/\//.test(p) ? p : `${STORAGE_BASE}/${String(p).replace(/^\/\/+/, '')}`));

const SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};
function frameStyle(f) {
  if (!f) return undefined;
  const st = {};
  if (f.bg && f.bg !== 'transparent') st.background = f.bg;
  if (f.padding) st.padding = `${f.padding}px`;
  const tl = f.radiusTL ?? f.radius ?? 0, tr = f.radiusTR ?? f.radius ?? 0;
  const br = f.radiusBR ?? f.radius ?? 0, bl = f.radiusBL ?? f.radius ?? 0;
  if (tl || tr || br || bl) { st.borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`; st.overflow = 'hidden'; }
  if (f.shadow && SHADOWS[f.shadow]) st.boxShadow = SHADOWS[f.shadow];
  const mT = f.marginT ?? 0, mR = f.marginR ?? 0, mB = f.marginB ?? 0, mL = f.marginL ?? 0;
  if (mT || mR || mB || mL) st.margin = `${mT}px ${mR}px ${mB}px ${mL}px`;
  return Object.keys(st).length ? st : undefined;
}

function SmartLink({ to, style, children, onClick, onNavigate, ...rest }) {
  const handle = e => { if (onClick) onClick(e); if (!e.defaultPrevented && onNavigate) onNavigate(); };
  if (!to) return <span style={style} {...rest}>{children}</span>;
  if (/^https?:\/\//.test(to)) return <a href={to} target="_blank" rel="noreferrer" style={style} onClick={handle} {...rest}>{children}</a>;
  return <Link to={to} style={style} onClick={handle} {...rest}>{children}</Link>;
}

const SOCIAL_ICONS = { instagram: Camera, telegram: Send, whatsapp: MessageCircle, phone: Phone, mail: Mail, website: Globe };

/* Build a nestable tree from flat categories with parent_id (any depth) */
export function buildTree(cats) {
  const byId = {};
  (cats || []).forEach(c => { byId[c.id] = { ...c, children: [] }; });
  const roots = [];
  (cats || []).forEach(c => {
    const node = byId[c.id];
    if (c.parent_id && byId[c.parent_id]) byId[c.parent_id].children.push(node);
    else roots.push(node);
  });
  return roots;
}

/* Recursive category links with indent */
function CatTree({ nodes, depth = 0, getStyle, onNavigate }) {
  if (!nodes || !nodes.length || depth > 3) return null;
  return (
    <>
      {nodes.map(n => (
        <div key={n.id}>
          <SmartLink to={n.url || `/category/${n.slug}`} onClick={onNavigate} style={getStyle(depth)}>{n.name}</SmartLink>
          {!!n.children.length && (
            <div style={{ paddingRight: 12, borderRight: '2px solid var(--border)', marginTop: 2, display: 'flex', flexDirection: 'column' }}>
              <CatTree nodes={n.children} depth={depth + 1} getStyle={getStyle} onNavigate={onNavigate} />
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/* On mobile, menu/mega widgets live in the drawer — strip them from the bar
   unless the admin enabled "show on mobile" for that widget. */
export function stripNavForMobile(rows) {
  return (rows || []).map(row => ({
    ...row,
    columns: (row.columns || []).map(col => ({
      ...col,
      widgets: (col.widgets || []).filter(w => {
        if (w.enabled === false) return false;
        if ((w.type === 'menu' || w.type === 'mega_menu') && !(w.settings || {}).showOnMobile) return false;
        return true;
      }),
    })).filter(col => col.widgets.length > 0),
  })).filter(row => row.columns.length > 0);
}

/* ================= row shell ================= */
function ChromeRow({ row, children }) {
  const rs = row.settings || {};
  const cols = row.columns || [];
  const tl = rs.radiusTL ?? rs.radius ?? 0, tr = rs.radiusTR ?? rs.radius ?? 0;
  const br = rs.radiusBR ?? rs.radius ?? 0, bl = rs.radiusBL ?? rs.radius ?? 0;
  const boxR = (tl || tr || br || bl) ? `${tl}px ${tr}px ${br}px ${bl}px` : undefined;
  const padT = rs.padT ?? rs.padY ?? 12, padB = rs.padB ?? rs.padY ?? 12;
  const padR = rs.padR ?? 0, padL = rs.padL ?? 0;
  // Unified row style (same «استایل کامل» as home rows): enables real glass bars.
  const rowUnified = widgetStyleObj(rs.style);
  // Custom per-column widths (settings.width در درصد، مثل ردیف‌های صفحه اصلی)
  const colWidths = cols.map(c => Number(c.settings?.width) || 0);
  const hasCustomWidths = colWidths.some(w => w > 0);
  const widthTemplate = hasCustomWidths
    ? cols.map((c, i) => `${Number(c.settings?.width) > 0 ? Number(c.settings.width) : (100 / cols.length)}%`).join(' ')
    : undefined;
  return (
    <section style={{
      background: rs.bg && rs.bg !== 'transparent' ? rs.bg : undefined,
      padding: `${padT}px ${padR}px ${padB}px ${padL}px`,
      borderRadius: boxR, boxShadow: rs.shadow && SHADOWS[rs.shadow],
      overflow: (boxR || rs.shadow) ? 'hidden' : undefined,
      ...rowUnified,
    }}>
      <div className="hb-chrome">
        <div className={`hb-cols hb-cols-${Math.min(cols.length, 4)}${rs.keepCols ? ' keep-cols' : ''}${hasCustomWidths ? ' hb-cols-custom' : ''}`} style={{ gap: rs.gap ?? 16, alignItems: 'center', ...(widthTemplate ? { '--hb-w': widthTemplate } : {}) }}>
          {cols.map((col, ci) => {
            const cs = col.settings || {};
            const ctl = cs.radiusTL ?? cs.radius ?? 0, ctr = cs.radiusTR ?? cs.radius ?? 0;
            const cbr = cs.radiusBR ?? cs.radius ?? 0, cbl = cs.radiusBL ?? cs.radius ?? 0;
            const cboxR = (ctl || ctr || cbr || cbl) ? `${ctl}px ${ctr}px ${cbr}px ${cbl}px` : undefined;
            const horizontal = cs.direction === 'row';
            return (
              <div key={ci} style={{
                minWidth: 0, display: 'flex', flexDirection: horizontal ? 'row' : 'column', gap: 10, justifyContent: cs.justify || 'center',
                alignItems: horizontal ? 'center' : undefined, flexWrap: cs.noWrap ? 'nowrap' : (horizontal ? 'wrap' : undefined),
                background: cs.bg && cs.bg !== 'transparent' ? cs.bg : undefined,
                padding: cs.padding ? `${cs.padding}px` : undefined,
                borderRadius: cboxR, boxShadow: cs.shadow && SHADOWS[cs.shadow],
                overflow: (cboxR || cs.shadow) ? 'hidden' : undefined,
              }}>
                {children(col, ci)}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ================= customer auth state (shared) ================= */
export function customerToken() {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
}
export function clearCustomer() {
  try { localStorage.removeItem('customer_token'); localStorage.removeItem('customer_phone'); } catch {}
}
const CustomerCtx = React.createContext({ customer: null, checking: true, setCustomer: () => {}, refreshCustomer: () => {} });
export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [checking, setChecking] = useState(true);
  const refreshCustomer = React.useCallback(() => {
    const t = customerToken();
    if (!t) { setCustomer(null); setChecking(false); return; }
    axios.get('/auth/me', { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setCustomer(r.data.user || r.data))
      .catch(() => { clearCustomer(); setCustomer(null); })
      .finally(() => setChecking(false));
  }, []);
  useEffect(() => { refreshCustomer(); }, [refreshCustomer]);
  useEffect(() => {
    const onChange = () => refreshCustomer();
    window.addEventListener('customer-changed', onChange);
    return () => window.removeEventListener('customer-changed', onChange);
  }, [refreshCustomer]);
  return <CustomerCtx.Provider value={{ customer, checking, setCustomer, refreshCustomer }}>{children}</CustomerCtx.Provider>;
}
export function useCustomer() {
  return React.useContext(CustomerCtx);
}

/* Sticky announcement bar (header): always pinned to the top of the viewport */
function WAnnouncement({ settings: s, wid }) {
  const [gone, setGone] = useState(false);
  const key = `ann dismissed ${(wid || '')} ${(s.text || '')}`;
  useEffect(() => {
    try { if (localStorage.getItem(key) === '1') setGone(true); } catch {}
  }, [key]);
  if (!s.text || gone) return null;
  const dismiss = () => {
    setGone(true);
    try { localStorage.setItem(key, '1'); } catch {}
  };
  const inner = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: s.bold === false ? 400 : 700 }}>
      {s.icon && <span>{s.icon}</span>}
      <span>{s.text}</span>
      {s.link_text && <span style={{ textDecoration: 'underline' }}>{s.link_text}</span>}
    </span>
  );
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 120, background: s.bg || 'var(--primary)', color: s.color || 'var(--on-primary)', fontSize: s.fontSize ? `${s.fontSize}px` : '0.82rem', textAlign: 'center', padding: '9px 44px 9px 16px' }}>
      {s.link ? <SmartLink to={s.link} style={{ color: 'inherit', textDecoration: 'none' }}>{inner}</SmartLink> : inner}
      {s.dismissible !== false && (
        <button onClick={dismiss} aria-label="بستن اطلاعیه"
          style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.18)', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
          ×
        </button>
      )}
    </div>
  );
}

/* ================= header widgets ================= */
function WTopbar({ settings: s }) {
  if (!s.text && !(s.links || []).length) return null;
  return (
    <div style={{ background: s.bg || 'var(--primary)', color: s.color || 'var(--on-primary)', fontSize: s.fontSize ? `${s.fontSize}px` : '0.78rem', textAlign: s.align || 'center', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
      {s.text && <span>{s.text}</span>}
      {(s.links || []).map((l, i) => <SmartLink key={i} to={l.url} style={{ color: 'inherit', textDecoration: 'underline', fontSize: 'inherit' }}>{l.label}</SmartLink>)}
    </div>
  );
}

function WBrand({ settings: s }) {
  const Icon = ICONS[s.icon] || null;
  const inner = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      {s.logo ? (
        <img src={resolveSrc(s.logo)} alt={s.name || ''} style={{ height: (s.size || 22) * 1.7, display: 'block' }} />
      ) : Icon ? (
        <Icon size={(s.size || 22) * 1.2} color={s.iconColor || s.color || 'var(--primary)'} />
      ) : null}
      {s.name && <span className="serif" style={{ fontSize: s.size ? `${s.size}px` : '1.35rem', fontWeight: 500, color: s.color || 'var(--primary)' }}>{s.name}</span>}
    </span>
  );
  return <SmartLink to={s.link || '/'} style={{ textDecoration: 'none' }}>{inner}</SmartLink>;
}

/* Resolve menu items: manual links + typed items (page / single / auto category lists) */
export function resolveMenuItems(items, ctx = {}) {
  const cats = ctx.categories || [];
  const postCats = ctx.postCategories || [];
  const flat = buildTree(cats);
  const findCat = ref => cats.find(c => String(c.id) === String(ref));
  const out = [];
  for (const it of (items || [])) {
    const t = it.menuType || 'link';
    if (t === 'product_cats') {
      for (const root of flat) {
        out.push({
          label: it.label ? `${it.label}: ${root.name}` : root.name,
          url: `/category/${root.slug}`,
          children: (root.children || []).map(ch => ({ label: ch.name, url: `/category/${ch.slug}` })),
        });
      }
      continue;
    }
    if (t === 'post_cats') {
      for (const c of postCats) {
        out.push({ label: it.label ? `${it.label}: ${c.name}` : c.name, url: `/blog?category=${c.slug}` });
      }
      continue;
    }
    if (t === 'page') {
      if (!it.ref) continue;
      out.push({ label: it.label || it.ref, url: `/${it.ref}`, children: resolveMenuItems(it.children, ctx) });
      continue;
    }
    if (t === 'product_cat') {
      const c = findCat(it.ref);
      if (!c) continue;
      out.push({ label: it.label || c.name, url: `/category/${c.slug}`, children: resolveMenuItems(it.children, ctx) });
      continue;
    }
    if (t === 'post_cat') {
      if (!it.ref) continue;
      const c = postCats.find(x => x.slug === it.ref);
      out.push({ label: it.label || (c ? c.name : it.ref), url: `/blog?category=${it.ref}`, children: resolveMenuItems(it.children, ctx) });
      continue;
    }
    out.push({ ...it, children: resolveMenuItems(it.children, ctx) });
  }
  return out;
}

function WMenu({ settings: s, ctx = {} }) {
  const [open, setOpen] = useState(null);
  const items = resolveMenuItems(s.items || [], ctx);
  if (!items.length) return null;
  const Icon = ICONS[s.icon] || null;
  const iconEl = Icon ? <Icon size={15} color={s.iconColor || s.color || 'var(--fg)'} style={{ flexShrink: 0 }} /> : null;
  const linkStyle = { color: s.color || 'var(--fg)', textDecoration: 'none', fontSize: s.fontSize ? `${s.fontSize}px` : '0.95rem', fontWeight: 500, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 };
  return (
    <nav onMouseLeave={() => setOpen(null)} style={{ display: 'flex', alignItems: 'center', gap: s.gap ?? 24, justifyContent: s.align === 'center' ? 'center' : s.align === 'left' ? 'flex-start' : 'flex-start', flexWrap: 'wrap', ...(s.bg && s.bg !== 'transparent' ? { background: s.bg, borderRadius: s.radius ?? 10, padding: '8px 14px' } : {}) }}>
      {items.map((it, i) => (it.children && it.children.length) ? (
        <div key={i} style={{ position: 'relative' }} onMouseEnter={() => setOpen(i)}>
          <SmartLink to={it.url} style={linkStyle}>{iconEl}{it.label}<ChevronDown size={14} /></SmartLink>
          {open === i && (
            <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 'var(--shape-card)', boxShadow: '0 8px 30px rgba(0,0,0,0.10)', minWidth: 200, padding: 8, zIndex: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {it.children.map((ch, j) => (
                <SmartLink key={j} to={ch.url} style={{ color: 'var(--muted-fg)', textDecoration: 'none', fontSize: '0.85rem', padding: '8px 12px', borderRadius: 'var(--shape-btn)' }}>{ch.label}</SmartLink>
              ))}
            </div>
          )}
        </div>
      ) : (
        <SmartLink key={i} to={it.url} style={linkStyle}>{iconEl}{it.label}</SmartLink>
      ))}
    </nav>
  );
}

/** Digikala-style desktop mega menu: right sidebar of root categories, hover reveals subgroups on the left (RTL). */
function MegaMenuDigikala({ roots, color, onNavigate }) {
  const [activeId, setActiveId] = useState(null);
  const list = roots || [];
  const active = list.find(r => r.id === activeId) || list[0] || null;
  const kids = (active && active.children) || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'row', direction: 'rtl', minHeight: 320, maxHeight: '70vh' }}>
      <div style={{ width: 230, flexShrink: 0, borderLeft: '1px solid var(--border)', paddingLeft: 12, overflowY: 'auto' }}>
        {list.map(root => {
          const on = active && root.id === active.id;
          return (
            <SmartLink key={root.id} to={`/category/${root.slug}`} onNavigate={onNavigate}
              onMouseEnter={() => setActiveId(root.id)} onFocus={() => setActiveId(root.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none',
                padding: '10px 12px', borderRadius: 'var(--shape-btn)', fontSize: '0.88rem', fontWeight: on ? 700 : 500,
                color: on ? (color || 'var(--primary)') : 'var(--fg)',
                background: on ? 'var(--muted)' : 'transparent',
              }}>
              {(root.image_path) && (
                <img src={resolveSrc(root.image_path)} alt="" loading="lazy" style={{ width: 26, height: 26, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
              )}
              <span style={{ flex: 1 }}>{root.name}</span>
              <ChevronDown size={14} style={{ transform: 'rotate(90deg)', opacity: 0.6, flexShrink: 0 }} />
            </SmartLink>
          );
        })}
        <Link to="/products" onClick={onNavigate} style={{ textDecoration: 'none', display: 'block', marginTop: 8 }}>
          <div style={{ borderRadius: 'var(--shape-btn)', background: 'var(--primary)', color: 'var(--on-primary)', padding: '10px 12px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>همه محصولات</div>
        </Link>
      </div>
      <div style={{ flex: 1, paddingRight: 20, overflowY: 'auto' }}>
        {active && (
          <SmartLink to={`/category/${active.slug}`} onNavigate={onNavigate} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: color || 'var(--primary)', fontWeight: 700, fontSize: '0.92rem', marginBottom: 12 }}>
            همه محصولات {active.name}<ChevronDown size={14} style={{ transform: 'rotate(90deg)' }} />
          </SmartLink>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 18 }}>
          {kids.map(sub => (
            <div key={sub.id}>
              <SmartLink to={`/category/${sub.slug}`} onNavigate={onNavigate} style={{ fontWeight: 700, fontSize: '0.86rem', color: 'var(--fg)', textDecoration: 'none', display: 'block', marginBottom: 4, paddingRight: 8, borderRight: '2px solid var(--primary)' }}>{sub.name}</SmartLink>
              {((sub.children) || []).map(g => (
                <SmartLink key={g.id} to={`/category/${g.slug}`} onNavigate={onNavigate} style={{ color: 'var(--muted-fg)', textDecoration: 'none', fontSize: '0.82rem', padding: '4px 0', display: 'block' }}>{g.name}</SmartLink>
              ))}
            </div>
          ))}
          {kids.length === 0 && (
            <div style={{ color: 'var(--muted-fg)', fontSize: '0.85rem' }}>زیرگروهی ثبت نشده است.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function WMegaMenu({ settings: s, categories, postCategories }) {
  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(0);
  const btnRef = useRef(null);
  const mode = s.mode || 'categories';
  const title = s.title || 'دسته‌بندی‌ها';
  const Icon = ICONS[s.icon] || null;
  const hasBtnBg = s.btnBg && s.btnBg !== 'transparent';
  const isMobilePanel = typeof window !== 'undefined' && window.innerWidth < 768;
  const setOpenMeasured = o => {
    if (o && btnRef.current) {
      try { setPanelTop(btnRef.current.getBoundingClientRect().bottom + (isMobilePanel ? 8 : 0)); } catch {}
    }
    setOpen(o);
  };
  const closeTimer = useRef(null);
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 180); };
  useEffect(() => () => cancelClose(), []);
  useEffect(() => {
    if (!open || isMobilePanel) return;
    const on = () => setOpen(false);
    window.addEventListener('scroll', on, true);
    return () => window.removeEventListener('scroll', on, true);
  }, [open]);
  return (
    <div onMouseLeave={scheduleClose} onMouseEnter={cancelClose} style={{ position: 'relative' }}>
      <button ref={btnRef} onMouseEnter={() => { if (!isMobilePanel) { cancelClose(); setOpenMeasured(true); } }} onClick={() => setOpenMeasured(!open)}
        style={{ background: hasBtnBg ? s.btnBg : 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: s.color || 'var(--fg)', fontSize: '0.95rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, padding: hasBtnBg ? '9px 16px' : '8px 4px', borderRadius: hasBtnBg ? (s.btnRadius ?? 'var(--shape-btn)') : 0, whiteSpace: 'nowrap' }}>
        {Icon && <Icon size={16} color={s.iconColor || s.color || 'var(--fg)'} style={{ flexShrink: 0 }} />}
        {title}<ChevronDown size={15} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div style={isMobilePanel
          ? { position: 'fixed', top: panelTop || 120, left: 12, right: 12, width: 'auto', maxHeight: '70vh', overflowY: 'auto', background: (s.panelBg && s.panelBg !== 'transparent') ? s.panelBg : 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 'var(--shape-card)', boxShadow: '0 16px 48px rgba(0,0,0,0.16)', padding: 20, zIndex: 300 }
          : (mode === 'categories'
            ? { position: 'fixed', top: panelTop || 120, left: 16, right: 16, width: 'auto', background: (s.panelBg && s.panelBg !== 'transparent') ? s.panelBg : 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 'var(--shape-card)', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', padding: 20, zIndex: 300 }
            : { position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: (s.panelBg && s.panelBg !== 'transparent') ? s.panelBg : 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 'var(--shape-card)', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', padding: 20, zIndex: 300, width: 'min(560px, 86vw)' })}>
          {mode === 'categories' ? (
            isMobilePanel ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
              {buildTree(categories).slice(0, s.limit || 8).map(root => (
                <div key={root.id}>
                  {(s.showImages !== false && root.image_path) && (
                    <Link to={`/category/${root.slug}`} onClick={() => setOpen(false)} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <img src={resolveSrc(root.image_path)} alt={root.name} loading="lazy" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block', borderRadius: 'var(--shape-card)', marginBottom: 8 }} />
                    </Link>
                  )}
                  <SmartLink to={`/category/${root.slug}`} onClick={() => setOpen(false)} style={{ fontWeight: 700, fontSize: '0.88rem', color: s.color || 'var(--primary)', textDecoration: 'none', display: 'block', marginBottom: 2 }}>{root.name}</SmartLink>
                  <CatTree nodes={root.children} depth={1} onNavigate={() => setOpen(false)}
                    getStyle={() => ({ color: 'var(--muted-fg)', textDecoration: 'none', fontSize: '0.82rem', padding: '5px 0', display: 'block' })} />
                </div>
              ))}
              <Link to="/products" onClick={() => setOpen(false)} style={{ textDecoration: 'none', alignSelf: 'start' }}>
                <div style={{ borderRadius: 'var(--shape-btn)', background: 'var(--primary)', color: 'var(--on-primary)', padding: '10px 12px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>همه محصولات</div>
              </Link>
            </div>
            ) : (
              <MegaMenuDigikala roots={buildTree(categories).slice(0, s.limit || 8)} color={s.color} onNavigate={() => setOpen(false)} />
            )
          ) : mode === 'post_categories' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
              {(postCategories || []).slice(0, s.limit || 8).map(c => (
                <SmartLink key={c.id} to={`/blog?category=${c.slug}`} onClick={() => setOpen(false)} style={{ textDecoration: 'none', color: 'inherit', display: 'block', background: 'var(--muted)', border: 'var(--shape-line) solid var(--border)', borderRadius: 'var(--shape-card)', padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: s.color || 'var(--primary)', marginBottom: 2 }}>{c.name}</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--muted-fg)' }}>{Number(c.posts_count || 0).toLocaleString('fa-IR')} مطلب</div>
                </SmartLink>
              ))}
              <Link to="/blog" onClick={() => setOpen(false)} style={{ textDecoration: 'none', alignSelf: 'start' }}>
                <div style={{ borderRadius: 'var(--shape-btn)', background: 'var(--primary)', color: 'var(--on-primary)', padding: '10px 12px', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>همه مطالب</div>
              </Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
              {(s.groups || []).map((g, i) => (
                <div key={i}>
                  {g.title && <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8, color: s.color || 'var(--primary)' }}>{g.title}</div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(g.links || []).map((l, j) => (
                      <SmartLink key={j} to={l.url} style={{ color: 'var(--muted-fg)', textDecoration: 'none', fontSize: '0.85rem' }}>{l.label}</SmartLink>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WSearch({ settings: s, ctx }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [selCats, setSelCats] = useState([]);
  const toggleCat = (id) => setSelCats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [selSort, setSelSort] = useState('');
  const [priceLo, setPriceLo] = useState('');
  const [priceHi, setPriceHi] = useState('');
  const [expanded, setExpanded] = useState({});
  const [bounds, setBounds] = useState({ min: 0, max: 50000000 });
  const [boundsOk, setBoundsOk] = useState(false);
  const [ftab, setFtab] = useState('cat');
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const onlyDigits = (v) => String(v || '').replace(/[^0-9]/g, '').slice(0, 12);
  const siteLayout = useSiteLayout();
  let vw = 1024;
  try { vw = siteLayout.vw || window.innerWidth || 1024; } catch {}
  const isMobile = vw < 768;
  const cats = (ctx && ctx.categories) || [];
  const catTree = buildTree(cats);
  const SORTS = [
    ['newest', 'جدیدترین'],
    ['price_asc', 'ارزان‌ترین'],
    ['price_desc', 'گران‌ترین'],
    ['name', 'نام'],
  ];
  const goSearch = (opts = {}) => {
    const query = opts.q !== undefined ? opts.q : q.trim();
    const catsSel = opts.cats !== undefined ? opts.cats : selCats;
    const cat = opts.cat !== undefined ? opts.cat : (catsSel.length === 1 ? catsSel[0] : '');
    const sort = opts.sort !== undefined ? opts.sort : selSort;
    const lo = opts.lo !== undefined ? opts.lo : onlyDigits(priceLo);
    const hi = opts.hi !== undefined ? opts.hi : onlyDigits(priceHi);
    const p = new URLSearchParams();
    if (query) p.set('q', query);
    if (catsSel.length > 1) p.set('categories', catsSel.join(','));
    else if (cat) p.set('category', cat);
    if (sort) p.set('sort', sort);
    if (lo) p.set('min_price', lo);
    if (hi) p.set('max_price', hi);
    setOpen(false);
    navigate(`/products${p.toString() ? `?${p.toString()}` : ''}`);
  };
  const clearFilters = () => { setSelCats([]); setSelSort(''); setPriceLo(''); setPriceHi(''); setQ(''); };
  const radioOuter = (on) => ({
    width: 22, height: 22, borderRadius: 999, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2px solid ' + (on ? 'var(--primary)' : 'var(--border)'), background: 'var(--card)',
  });
  const radioDot = { width: 10, height: 10, borderRadius: 999, background: 'var(--primary)' };
  const tabBtn = (on) => ({
    flex: 1, padding: '11px 4px', background: 'none', border: 'none', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 800, cursor: 'pointer',
    color: on ? 'var(--primary)' : 'var(--muted-fg)', borderBottom: on ? '2.5px solid var(--primary)' : '2.5px solid transparent',
  });
  const catRowStyle = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 4px', background: 'none', border: 'none', fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: 600, color: 'var(--fg)', cursor: 'pointer', textAlign: 'right' };
  const activeCount = (selCats.length ? 1 : 0) + (onlyDigits(priceLo) || onlyDigits(priceHi) ? 1 : 0) + (selSort ? 1 : 0);
  useEffect(() => {
    if (!open) return undefined;
    if (!boundsOk) {
      axios.get('/filter-facets').then(r => {
        const pr = r.data && r.data.price;
        if (pr && Number(pr.max) > 0) {
          setBounds({ min: Number(pr.min) || 0, max: Number(pr.max) });
          setBoundsOk(true);
        }
      }).catch(() => {});
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open ]);
  const checkOuter = (on) => ({
    width: 22, height: 22, borderRadius: 7, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2px solid ' + (on ? 'var(--primary)' : 'var(--border)'), background: on ? 'var(--primary)' : 'var(--card)',
  });
  const renderCatNode = (node, depth) => {
    const id = String(node.id);
    const on = selCats.includes(id);
    const kids = node.children || [];
    const open = !!expanded[id];
    const kidCount = kids.length;
    return (
      <div key={id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingRight: depth * 18 }}>
          <button type="button" onClick={() => toggleCat(id)} style={catRowStyle} aria-pressed={on}>
            <span style={checkOuter(on)}>{on && <Check size={14} color="var(--on-primary)" strokeWidth={3.5} />}</span>
            <span style={{ flex: 1, fontWeight: on ? 800 : 600, color: on ? 'var(--primary)' : 'var(--fg)' }}>{node.name}</span>
            {kidCount > 0 && <span style={{ fontSize: '0.7rem', color: 'var(--muted-fg)', background: 'var(--muted)', borderRadius: 99, padding: '1px 8px' }}>{kidCount}</span>}
          </button>
          {kidCount > 0 && (
            <button type="button" onClick={() => toggleExpand(id)} aria-label={open ? 'بستن زیردسته‌ها' : 'نمایش زیردسته‌ها'}
              style={{ width: 38, height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: open ? 'var(--muted)' : 'none', border: 'none', borderRadius: 10, color: 'var(--muted-fg)', cursor: 'pointer' }}>
              <ChevronDown size={18} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
            </button>
          )}
        </div>
        {open && kidCount > 0 && (
          <div style={{ marginRight: 11, paddingRight: 8, borderRight: '2px solid var(--primary)', borderRadius: 2 }}>
            {kids.map(k => renderCatNode(k, depth + 1))}
          </div>
        )}
      </div>
    );
  };
  const modalSheet = (
    <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)', direction: 'rtl', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} className="hb-sheet-up" style={{ background: 'var(--card)', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: 560, maxHeight: '92dvh', display: 'flex', flexDirection: 'column', boxShadow: '0 -12px 50px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px 0' }}><div style={{ width: 44, height: 5, borderRadius: 99, background: 'var(--border)', margin: '0 auto' }} /></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px 4px' }}>
          <form onSubmit={e => { e.preventDefault(); goSearch(); }} style={{ display: 'flex', flex: 1, gap: 8 }}>
            <input value={q} onChange={e => setQ(e.target.value)} autoFocus placeholder={s.placeholder || 'جستجوی محصول...'}
              style={{ flex: 1, minWidth: 0, padding: '13px 16px', borderRadius: 'var(--shape-btn)', border: '1.5px solid var(--primary)', fontSize: '0.95rem', outline: 'none', background: 'var(--muted)', color: 'var(--fg)', fontFamily: 'inherit' }} />
            <button type="submit" aria-label="جستجو"
              style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 'var(--shape-btn)', border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Search size={20} />
            </button>
          </form>
          <button type="button" onClick={() => setOpen(false)} aria-label="بستن"
            style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 'var(--shape-btn)', border: 'var(--shape-line) solid var(--border)', background: 'transparent', color: 'var(--muted-fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ display: 'flex', padding: '4px 16px 0', borderBottom: '1px solid var(--border)' }} role="tablist" aria-label="فیلترها">
          {[['cat', 'دسته‌بندی'], ['price', 'بازه قیمت'], ['sort', 'مرتب‌سازی']].map(([v, t]) => (
            <button key={v} type="button" role="tab" aria-selected={ftab === v} onClick={() => setFtab(v)} style={tabBtn(ftab === v)}>{t}</button>
          ))}
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 16px 12px', minHeight: 180 }}>
          {ftab === 'cat' && catTree.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--muted-fg)', marginBottom: 4 }}>دسته‌بندی</div>
              {selCats.length > 0 && (
                <button type="button" onClick={() => setSelCats([])} style={{ ...catRowStyle, color: 'var(--muted-fg)', fontSize: '0.8rem' }}>
                  <span style={{ flex: 1 }}>حذف انتخاب‌ها ({selCats.length})</span>
                </button>
              )}
              {catTree.map(n => renderCatNode(n, 0))}
            </div>
          )}
          {ftab === 'price' && (() => {
            const bmin = Number(bounds.min) || 0;
            const bmax = Number(bounds.max) > bmin ? Number(bounds.max) : bmin + 1000000;
            const lo = priceLo === '' ? bmin : Math.min(Math.max(Number(priceLo), bmin), bmax);
            const hi = priceHi === '' ? bmax : Math.min(Math.max(Number(priceHi), bmin), bmax);
            const plo = Math.min(lo, hi), phi = Math.max(lo, hi);
            const pct = (v) => ((v - bmin) / (bmax - bmin)) * 100;
            const fmt = (v) => Number(v).toLocaleString('fa-IR');
            const setLo = (v) => setPriceLo(String(Math.min(Number(v), hi)));
            const setHi = (v) => setPriceHi(String(Math.max(Number(v), lo)));
            return (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--muted-fg)' }}>بازه قیمت</span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--primary)' }}>{fmt(plo)} تا {fmt(phi)} تومان</span>
                </div>
                <div style={{ position: 'relative', height: 36, direction: 'ltr' }}>
                  <div style={{ position: 'absolute', top: 15, right: 0, left: 0, height: 6, borderRadius: 99, background: 'var(--border)' }} />
                  <div style={{ position: 'absolute', top: 15, height: 6, borderRadius: 99, background: 'var(--primary)', left: pct(plo) + '%', right: (100 - pct(phi)) + '%' }} />
                  <input type="range" className="hb-range" aria-label="کف قیمت" min={bmin} max={bmax} step={Math.max(1, Math.round((bmax - bmin) / 100))} value={plo} onChange={e => setLo(e.target.value)} />
                  <input type="range" className="hb-range" aria-label="سقف قیمت" min={bmin} max={bmax} step={Math.max(1, Math.round((bmax - bmin) / 100))} value={phi} onChange={e => setHi(e.target.value)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <input value={priceLo} onChange={e => setPriceLo(onlyDigits(e.target.value))} inputMode="numeric" placeholder="از"
                    style={{ flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: 'var(--shape-btn)', border: '1.5px solid var(--border)', fontSize: '0.85rem', outline: 'none', background: 'var(--muted)', color: 'var(--fg)', fontFamily: 'inherit' }} />
                  <span style={{ color: 'var(--muted-fg)' }}>تا</span>
                  <input value={priceHi} onChange={e => setPriceHi(onlyDigits(e.target.value))} inputMode="numeric" placeholder="تا"
                    style={{ flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: 'var(--shape-btn)', border: '1.5px solid var(--border)', fontSize: '0.85rem', outline: 'none', background: 'var(--muted)', color: 'var(--fg)', fontFamily: 'inherit' }} />
                </div>
                {(priceLo !== '' || priceHi !== '') && (
                  <button type="button" onClick={() => { setPriceLo(''); setPriceHi(''); }} style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--muted-fg)', fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>حذف محدوده قیمت</button>
                )}
              </div>
            );
          })()}
          {ftab === 'sort' && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--muted-fg)', marginBottom: 8 }}>مرتب‌سازی</div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {SORTS.map(([v, label]) => (
                <button key={v} type="button" onClick={() => setSelSort(selSort === v ? '' : v)}
                  style={{ padding: '8px 15px', borderRadius: 999, border: '1.5px solid', borderColor: selSort === v ? 'var(--primary)' : 'var(--border)', background: selSort === v ? 'var(--primary)' : 'transparent', color: selSort === v ? 'var(--on-primary)' : 'var(--fg)', fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>{label}</button>
              ))}
            </div>
          </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px calc(16px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--card)' }}>
          <button type="button" onClick={() => goSearch()} style={{ flex: 1, minHeight: 50, borderRadius: 'var(--shape-btn)', border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'inherit', fontSize: '0.95rem', fontWeight: 800, cursor: 'pointer' }}>نمایش نتایج{activeCount > 0 ? ` (${activeCount})` : ''}</button>
          <button type="button" onClick={clearFilters} style={{ minHeight: 50, padding: '0 18px', borderRadius: 'var(--shape-btn)', border: '1.5px solid var(--border)', background: 'transparent', color: 'var(--muted-fg)', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>حذف فیلترها</button>
        </div>
      </div>
    </div>
  );
  // موبایل — حالت نوار تمام‌عرض: باکس لمسی که مودال را باز می‌کند (برای ردیف دوم هدر)
  if (isMobile && s.mobileVariant === 'bar') {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} aria-label="جستجو"
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: (s.radius ?? 'var(--shape-btn)'), border: 'var(--shape-line) solid var(--border)', background: 'var(--muted)', color: 'var(--muted-fg)', fontFamily: 'inherit', fontSize: '0.85rem', cursor: 'pointer' }}>
          <Search size={17} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.placeholder || 'جستجو در محصولات...'}</span>
        </button>
        {open && typeof document !== 'undefined' && createPortal(modalSheet, document.body)}
      </>
    );
  }
  // موبایل: فقط آیکون؛ با لمس، مودال جست‌وجو باز می‌شود
  if (isMobile) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} aria-label="جستجو"
          style={{ width: 42, height: 42, flexShrink: 0, borderRadius: (s.radius ?? 'var(--shape-btn)'), border: 'var(--shape-line) solid var(--border)', background: 'var(--card)', color: 'var(--primary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Search size={19} />
        </button>
        {open && typeof document !== 'undefined' && createPortal(modalSheet, document.body)}
      </>
    );
  }
  return (
    <form onSubmit={e => { e.preventDefault(); navigate(`/products?q=${encodeURIComponent(q.trim())}`); }}
      style={{ display: 'flex', width: '100%', maxWidth: s.maxWidth || 320 }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={s.placeholder || 'جستجوی محصول...'}
        style={{ flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: (s.radius ?? 'var(--shape-input)'), border: 'var(--shape-line-input) solid var(--border)', fontSize: '0.85rem', outline: 'none', background: 'var(--muted)', fontFamily: 'inherit' }} />
      <button type="submit" aria-label="جستجو"
        style={{ marginRight: 8, width: 42, height: 42, flexShrink: 0, borderRadius: (s.radius ?? 'var(--shape-btn)'), border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Search size={18} />
      </button>
    </form>
  );
}

function WHeaderButton({ settings: s }) {
  if (!s.label) return null;
  const solid = (s.variant || 'solid') === 'solid';
  return (
    <SmartLink to={s.url} style={{
      display: 'inline-block', textDecoration: 'none', padding: '10px 22px', borderRadius: s.radius ?? 'var(--shape-btn)',
      fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap',
      background: solid ? (s.bg || 'var(--primary)') : 'transparent',
      color: solid ? (s.color || 'var(--on-primary)') : (s.bg || 'var(--primary)'),
      border: solid ? 'none' : `1.5px solid ${s.bg || 'var(--primary)'}`,
    }}>{s.label}</SmartLink>
  );
}

/** Cart + compare icon actions with count badges (place next to account button) */
export function WShopActions({ settings: s }) {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [cmpCount, setCmpCount] = useState(0);
  const showCart = s.showCart !== false;
  const showCompare = s.showCompare !== false;
  const load = () => {
    const t = customerToken();
    if (!t) { setCartCount(0); setCmpCount(0); return; }
    const h = { headers: { Authorization: `Bearer ${t}` } };
    axios.get('/cart', h).then(r => setCartCount((r.data || []).reduce((a, c) => a + (c.qty || 1), 0))).catch(() => {});
    axios.get('/compare', h).then(r => setCmpCount((r.data || []).length)).catch(() => {});
  };
  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener('cart-updated', on);
    window.addEventListener('compare-updated', on);
    return () => { window.removeEventListener('cart-updated', on); window.removeEventListener('compare-updated', on); };
  }, []);
  if (!showCart && !showCompare) return null;
  const btn = {
    position: 'relative', width: 42, height: 42, borderRadius: s.radius ?? 'var(--shape-btn)', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    background: s.bg && s.bg !== 'transparent' ? s.bg : 'var(--card)',
    color: s.color || 'var(--primary)', border: 'var(--shape-line) solid var(--border)',
  };
  const badge = {
    position: 'absolute', top: -6, left: -6, minWidth: 20, height: 20, borderRadius: 999,
    background: 'var(--accent)', color: 'var(--on-primary)', fontSize: '0.68rem', fontWeight: 800,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
  };
  return (
    <span style={{ alignSelf: 'center', display: 'inline-flex', gap: 14 }}>
      {showCompare && (
        <button data-act="compare" onClick={() => window.dispatchEvent(new CustomEvent('open-compare'))} aria-label="مقایسه" title="مقایسه" style={btn}>
          <Scale size={19} />
          {cmpCount > 0 && <span style={badge}>{cmpCount > 9 ? '۹+' : cmpCount.toLocaleString('fa-IR')}</span>}
        </button>
      )}
      {showCart && (
        <button onClick={() => window.dispatchEvent(new CustomEvent('open-cart'))} aria-label="سبد خرید" title="سبد خرید" style={btn}>
          <ShoppingCart size={19} />
          {cartCount > 0 && <span style={badge}>{cartCount > 9 ? '۹+' : cartCount.toLocaleString('fa-IR')}</span>}
        </button>
      )}
    </span>
  );
}

/** Light/dark theme toggle (hidden unless the admin enables it in تم سایت). */
export function WThemeToggle({ settings: s }) {
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem('site-theme-mode') || 'auto';
      if (saved === 'light' || saved === 'dark') return saved;
    } catch {}
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  });
  const [allowed, setAllowed] = useState(!!s.alwaysShow);
  useEffect(() => {
    axios.get('/site-settings').then(r => setAllowed(!!r.data?.theme?.allow_toggle || !!s.alwaysShow)).catch(() => {});
    const on = () => {
      const cur = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
      setMode(cur);
    };
    window.addEventListener('site-theme-changed', on);
    return () => window.removeEventListener('site-theme-changed', on);
  }, [s.alwaysShow]);
  if (!allowed) return null;
  const dark = mode === 'dark';
  const flip = () => {
    const next = dark ? 'light' : 'dark';
    try { localStorage.setItem('site-theme-mode', next); } catch {}
    window.dispatchEvent(new Event('site-theme-changed'));
    setMode(next);
  };
  return (
    <span style={{ alignSelf: 'center' }}>
      <button onClick={flip} aria-label={dark ? 'حالت روشن' : 'حالت تیره'} title={dark ? 'حالت روشن' : 'حالت تیره'}
        style={{
          width: 42, height: 42, borderRadius: s.radius ?? 'var(--shape-btn)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          background: s.bg && s.bg !== 'transparent' ? s.bg : 'var(--card)',
          color: s.color || 'var(--primary)', border: 'var(--shape-line) solid var(--border)',
        }}>
        {dark ? <Sun size={19} /> : <Moon size={19} />}
      </button>
    </span>
  );
}

/** Login/register button: guest opens a modal, logged-in user goes to /account */
export function WAccountButton({ settings: s }) {
  const navigate = useNavigate();
  const { customer, setCustomer } = useCustomer();
  const [open, setOpen] = useState(false);
  const Icon = ICONS[s.icon || 'User'] || null;
  const hasBg = s.bg && s.bg !== 'transparent';
  const label = customer
    ? ((customer.name && customer.name !== customer.phone) ? customer.name : customer.phone)
    : (s.loggedOutText || 'ورود | ثبت‌نام');
  const iconOnly = !!s.iconOnly;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open ]);

  return (
    <>
      <span style={{ alignSelf: 'center' }}>
        <button onClick={() => { customer ? navigate('/account') : window.dispatchEvent(new CustomEvent('open-login')); }} title={iconOnly ? label : undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, cursor: 'pointer', fontFamily: 'inherit',
            fontSize: s.fontSize ? `${s.fontSize}px` : '0.88rem', fontWeight: 600, whiteSpace: 'nowrap',
            background: hasBg ? s.bg : 'var(--card)', color: s.color || 'var(--primary)',
            border: hasBg ? 'none' : 'var(--shape-line) solid var(--border)', borderRadius: s.radius ?? 'var(--shape-btn)',
            padding: iconOnly ? 0 : '0 16px', width: iconOnly ? 42 : undefined, height: 42, flexShrink: 0,
          }}>
          {Icon && <Icon size={17} color={s.iconColor || s.color || 'var(--primary)'} style={{ flexShrink: 0 }} />}
          {!iconOnly && label}
        </button>
      </span>
      {false && open && !customer && null}
    </>
  );
}

function WDrawerButton() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const sync = () => setOpen(window.__siteDrawer === true);
    window.addEventListener('toggle-drawer', sync);
    window.addEventListener('drawer-sync', sync);
    return () => { window.removeEventListener('toggle-drawer', sync); window.removeEventListener('drawer-sync', sync); };
  }, []);
  return (
    <button onClick={() => window.dispatchEvent(new CustomEvent('toggle-drawer'))} aria-label="منو"
      style={{ background: 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 10, width: 42, height: 42, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
      {open ? <X size={22} color="var(--primary)" /> : <MenuIcon size={22} color="var(--primary)" />}
    </button>
  );
}

function WContact({ settings: s }) {
  const color = s.color || 'var(--fg)';
  const fs = s.fontSize ? `${s.fontSize}px` : '0.85rem';
  const ic = s.iconColor || 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      {s.phone && <a href={`tel:${s.phone.replace(/\s/g, '')}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, textDecoration: 'none', fontSize: fs, fontWeight: 600 }}><Phone size={15} color={ic} /> <span dir="ltr">{s.phone}</span></a>}
      {s.email && <a href={`mailto:${s.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, textDecoration: 'none', fontSize: fs }}><Mail size={15} color={ic} /> {s.email}</a>}
    </div>
  );
}

function WSocials({ settings: s }) {
  const items = s.items || [];
  if (!items.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: s.align === 'center' ? 'center' : s.align === 'left' ? 'flex-start' : 'flex-start', flexWrap: 'wrap' }}>
      {items.map((it, i) => {
        const Cmp = SOCIAL_ICONS[it.network] || Globe;
        const size = s.size || 18;
        return (
          <SmartLink key={i} to={it.url} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: size + 20, height: size + 20, borderRadius: '50%',
            background: s.bg && s.bg !== 'transparent' ? s.bg : 'var(--muted)',
            color: s.color || 'var(--primary)',
          }}>{<Cmp size={size} />}</SmartLink>
        );
      })}
    </div>
  );
}

/* ================= footer widgets ================= */
function WBrandAbout({ settings: s }) {
  const Icon = ICONS[s.icon] || null;
  return (
    <div>
      <SmartLink to={s.link || '/'} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {s.logo ? <img src={resolveSrc(s.logo)} alt={s.name || ''} style={{ height: (s.size || 22) * 1.7, display: 'block' }} />
          : Icon ? <Icon size={(s.size || 22) * 1.2} color={s.iconColor || s.color || 'var(--card-fg)'} /> : null}
        {s.name && <span className="serif" style={{ fontSize: s.size ? `${s.size}px` : '1.3rem', fontWeight: 500, color: s.color || 'var(--card-fg)' }}>{s.name}</span>}
      </SmartLink>
      {s.about && <p style={{ fontSize: '0.9rem', lineHeight: 1.9, color: s.color ? `${s.color}CC` : 'var(--muted-fg)', maxWidth: 420, margin: 0 }}>{s.about}</p>}
    </div>
  );
}

function WLinkList({ settings: s }) {
  return (
    <div>
      {s.title && <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 12, color: s.titleColor || 'var(--card-fg)' }}>{s.title}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(s.links || []).map((l, i) => (
          <SmartLink key={i} to={l.url} style={{ color: s.color || 'var(--muted-fg)', textDecoration: 'none', fontSize: '0.9rem' }}>{l.label}</SmartLink>
        ))}
      </div>
    </div>
  );
}

function WContactInfo({ settings: s }) {
  const color = s.color || 'var(--muted-fg)';
  const ic = s.iconColor || 'var(--accent)';
  const row = { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.88rem', lineHeight: 1.8, color };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {s.address && <div style={row}><MapPin size={16} color={ic} style={{ flexShrink: 0, marginTop: 4 }} /> {s.address}</div>}
      {s.phone && <a href={`tel:${s.phone.replace(/\s/g, '')}`} style={{ ...row, textDecoration: 'none' }}><Phone size={16} color={ic} style={{ flexShrink: 0, marginTop: 4 }} /> <span dir="ltr">{s.phone}</span></a>}
      {s.email && <a href={`mailto:${s.email}`} style={{ ...row, textDecoration: 'none' }}><Mail size={16} color={ic} style={{ flexShrink: 0, marginTop: 4 }} /> {s.email}</a>}
      {s.hours && <div style={row}><Clock size={16} color={ic} style={{ flexShrink: 0, marginTop: 4 }} /> {s.hours}</div>}
    </div>
  );
}

function WNewsletter({ settings: s }) {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post('/contact', { name: 'عضویت خبرنامه', email, message: 'درخواست عضویت در خبرنامه فوتر' });
      setMsg('عضویت شما ثبت شد. سپاس!');
      setEmail('');
    } catch { setMsg('خطایی رخ داد. دوباره تلاش کنید.'); }
    setBusy(false);
  };
  return (
    <div>
      {s.title && <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 6, color: s.titleColor || 'var(--card-fg)' }}>{s.title}</div>}
      {s.subtitle && <div style={{ fontSize: '0.85rem', marginBottom: 12, color: 'var(--muted-fg)' }}>{s.subtitle}</div>}
      <form onSubmit={submit} style={{ display: 'flex', gap: 8 }}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ایمیل شما" required
          style={{ flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: 10, border: 'var(--shape-line) solid var(--border)', fontSize: '0.85rem', outline: 'none', background: 'var(--card)', color: 'var(--fg)', fontFamily: 'inherit' }} dir="ltr" />
        <button type="submit" disabled={busy}
          style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: s.bg || 'var(--accent)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: busy ? 0.6 : 1 }}>
          {s.buttonText || 'عضویت'}
        </button>
      </form>
      {msg && <div style={{ fontSize: '0.8rem', marginTop: 8, color: '#86efac' }}>{msg}</div>}
    </div>
  );
}

function WCopyright({ settings: s }) {
  const text = (s.text || '© {year} — تمامی حقوق محفوظ است.').replace('{year}', new Date().getFullYear());
  return (
    <div style={{ textAlign: s.align || 'center', color: s.color || 'var(--muted-fg)', fontSize: s.fontSize ? `${s.fontSize}px` : '0.85rem', borderTop: '1px solid var(--border)', paddingTop: 18 }}>
      {text}
    </div>
  );
}

/* ================= widget switch ================= */
function renderChromeWidget(w, ctx) {
  const s = w.settings || {};
  let el;
  switch (w.type) {
    case 'announcement': el = <WAnnouncement settings={s} wid={w.id} />; break;
    case 'topbar': el = <WTopbar settings={s} />; break;
    case 'brand': el = <WBrand settings={s} />; break;
    case 'menu': el = <WMenu settings={s} ctx={ctx} />; break;
    case 'mega_menu': el = <WMegaMenu settings={s} categories={ctx.categories} postCategories={ctx.postCategories} />; break;
    case 'search': el = <WSearch settings={s} ctx={ctx} />; break;
    case 'header_button': el = <WHeaderButton settings={s} />; break;
    case 'account_button': el = <WAccountButton settings={s} />; break;
    case 'drawer_button': el = <WDrawerButton />; break;
    case 'shop_actions': el = <WShopActions settings={s} />; break;
    case 'theme_toggle': el = <WThemeToggle settings={s} />; break;
    case 'contact': el = <WContact settings={s} />; break;
    case 'socials': el = <WSocials settings={s} />; break;
    case 'brand_about': el = <WBrandAbout settings={s} />; break;
    case 'link_list': el = <WLinkList settings={s} />; break;
    case 'contact_info': el = <WContactInfo settings={s} />; break;
    case 'newsletter': el = <WNewsletter settings={s} />; break;
    case 'copyright': el = <WCopyright settings={s} />; break;
    case 'text': el = <WidgetText settings={s} />; break;
    case 'image': el = <WidgetImage settings={s} />; break;
    case 'divider': el = <WidgetDivider settings={s} />; break;
    case 'html': el = <WidgetHtml settings={s} />; break;
    default: return null;
  }
  if (!el) return null;
  // Unified style (settings.style) applies to every chrome widget incl. topbar
  const us = widgetStyleObj(s.style);
  if (w.type === 'topbar') return (us && Object.keys(us).length) ? <div style={us}>{el}</div> : el;
  const fs = frameStyle(s.frame);
  const wrap = { ...fs, ...us };
  if (!wrap || !Object.keys(wrap).length) return el;
  return <div style={wrap}>{el}</div>;
}

export function ChromeRows({ rows, ctx }) {
  return (
    <>
      {(rows || []).filter(r => r.enabled !== false).map((row, r) => (
        <ChromeRow key={row.id || r} row={row}>
          {(col) => (col.widgets || []).filter(w => w.enabled !== false).map((w, i) => (
            <span key={w.id || i} data-wtype={w.type} style={{ display: 'contents' }}>{renderChromeWidget(w, ctx)}</span>
          ))}
        </ChromeRow>
      ))}
    </>
  );
}

/* ================= layout data =================
 * تور ایمنی هدر: ستونی که جهت چیدمان ندارد (مثلاً با تغییر تعداد ستون‌ها در پنل پاک شده)
 * و بیش از یک ویجت دارد، هرگز نباید عمودی تلنبار شود — افقی فاصله‌دار می‌چیند.
 * تنظیمات صریح کاربر دست‌نخورده می‌ماند. */
/* ویجت‌های نواری هدر (در برابر متن/بنر تمام‌عرض) */
const HEADER_BAR_TYPES = ['brand', 'menu', 'search', 'account_button', 'shop_actions', 'theme_toggle', 'drawer_button', 'header_button', 'contact', 'socials'];
/* موبایل: همه ویجت‌های نواری هدر در یک نوار افقی واحد — هر طور که در پنل ستون‌بندی شده باشند.
 * ستون‌های تک‌ویجتی در موبایل زیر هم می‌افتند و هدر را خراب می‌کنند؛ این تابع نیت «نوار» را حفظ می‌کند.
 * ردیف‌های متنی/غیرنواری (اطلاعیه و...) دست‌نخورده و تمام‌عرض می‌مانند. فقط موبایل؛ دسکتاپ عین پنل. */
function flattenMobileBar(rows) {
  const kept = [];
  const bar = [];
  (rows || []).forEach(row => {
    const noMerge = (row.settings && row.settings.noMerge) === true;
    const ws = (row.columns || []).flatMap(c => c.widgets || []).filter(w => w.enabled !== false);
    if (!noMerge && ws.length > 0 && ws.every(w => HEADER_BAR_TYPES.includes(w.type))) {
      bar.push(...ws);
    } else {
      kept.push(row);
    }
  });
  if (bar.length <= 1) return rows;
  const brand = bar.filter(w => w.type === 'brand' || w.type === 'menu');
  const rest = bar.filter(w => w.type !== 'brand' && w.type !== 'menu' && w.type !== 'drawer_button');
  const drawer = bar.filter(w => w.type === 'drawer_button');
  kept.push({
    id: 'mbar', enabled: true, settings: { padT: 14, padB: 16, gap: 12 },
    columns: [{ settings: { direction: 'row', justify: 'space-between', noWrap: true }, widgets: [...brand, ...rest, ...drawer] }],
  });
  return kept;
}
function headerSafeRows(rows) {
  const normSet = (x) => (Array.isArray(x) || !x ? {} : x);
  return (rows || []).map(row => ({
    ...row,
    settings: normSet(row.settings),
    columns: (row.columns || []).map(col => {
      const cs = normSet(col.settings);
      const vis = (col.widgets || []).filter(w => w.enabled !== false);
      if (cs.direction === undefined && vis.length > 1) {
        return { ...col, settings: { direction: 'row', justify: 'space-between', ...cs } };
      }
      return { ...col, settings: cs };
    }),
  }));
}
function pickPlatform(obj, vw) {
  const key = vw < 768 ? 'mobile' : vw < 1024 ? 'tablet' : 'desktop';
  return obj?.[key] ?? obj?.desktop;
}

/* Shared layout fetch: one network call no matter how many components use it */
let layoutCacheData = null;
let layoutPromise = null;
function loadLayoutShared() {
  if (layoutCacheData) return Promise.resolve(layoutCacheData);
  if (!layoutPromise) {
    layoutPromise = axios.get('/layout').then(r => {
      layoutCacheData = r.data;
      try { localStorage.setItem('site_layout_cache', JSON.stringify(r.data)); } catch {}
      return layoutCacheData;
    }).catch(err => { layoutPromise = null; throw err; });
  }
  return layoutPromise;
}

export function useSiteLayout() {
  const [layout, setLayout] = useState(() => {
    if (layoutCacheData) return layoutCacheData;
    try { return JSON.parse(localStorage.getItem('site_layout_cache') || 'null'); }
    catch { return null; }
  });
  const [ready, setReady] = useState(false);
  const [vw, setVw] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const f = () => setVw(window.innerWidth);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  useEffect(() => {
    let live = true;
    loadLayoutShared()
      .then(d => { if (live) { setLayout(d); setReady(true); } })
      .catch(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, []);
  return { layout, vw, ready };
}

/* Full-screen customizable site loader (admin designs it in هدر و فوتر > لودینگ) */
export function SiteLoader() {
  const { layout, ready } = useSiteLayout();
  const [minDone, setMinDone] = useState(false);
  const [gone, setGone] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMinDone(true), 700); return () => clearTimeout(t); }, []);
  const show = !(ready && minDone);
  useEffect(() => {
    if (!show) { const t = setTimeout(() => setGone(true), 400); return () => clearTimeout(t); }
  }, [show]);
  if (gone) return null;
  const cfg = layout?.loading || {};
  const accent = cfg.spinnerColor || 'var(--primary)';
  const eff = (cfg.style || 'spinner') === 'logo' && !cfg.logo ? 'spinner' : (cfg.style || 'spinner');
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: cfg.bg || 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, transition: 'opacity 0.35s', opacity: show ? 1 : 0, pointerEvents: show ? 'auto' : 'none' }}>
      {cfg.logo && <img src={resolveSrc(cfg.logo)} alt="" style={{ width: 120, maxWidth: '50vw', animation: eff === 'logo' ? 'hb-pulse 1.6s ease infinite' : 'none' }} />}
      {eff === 'spinner' && <div style={{ width: 46, height: 46, borderRadius: '50%', border: '4px solid var(--border)', borderTopColor: accent, animation: 'hb-spin 0.9s linear infinite' }} />}
      {eff === 'bars' && (
        <div style={{ width: 'min(320px, 70vw)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {['100%', '80%', '60%'].map((w, i) => <div key={i} className="hb-loader-bar" style={{ height: 14, width: w }} />)}
        </div>
      )}
      {!!cfg.text && <div style={{ color: accent, fontSize: '0.9rem', fontWeight: 600 }}>{cfg.text}</div>}
    </div>
  );
}

/* ================= chrome loading skeletons =================
 * Shown while the layout is being fetched so the header/footer
 * area never flashes empty. Pure shimmer blocks, no network. */
const skm = { background: 'linear-gradient(90deg, var(--muted) 25%, color-mix(in srgb, var(--muted), var(--fg) 10%) 37%, var(--muted) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite', borderRadius: 10 };

export function HeaderSkeleton() {
  return (
    <header aria-hidden style={{ position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ background: 'var(--muted)', padding: '8px 16px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ ...skm, width: 'min(420px, 80%)', height: 12 }} />
      </div>
      <div style={{ background: 'var(--card, #fff)', borderBottom: '1px solid var(--border, #E4E4E7)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ ...skm, width: 130, height: 30, borderRadius: 8 }} />
        <div style={{ flex: 1, display: 'flex', gap: 14, justifyContent: 'center' }}>
          {[120, 90, 100].map((w, i) => <div key={i} style={{ ...skm, width: w, height: 14, borderRadius: 7 }} />)}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ ...skm, width: 42, height: 42, borderRadius: 12 }} />
          <div style={{ ...skm, width: 42, height: 42, borderRadius: 12 }} />
          <div style={{ ...skm, width: 110, height: 42, borderRadius: 10 }} />
        </div>
      </div>
    </header>
  );
}

export function FooterSkeleton() {
  return (
    <footer aria-hidden style={{ marginTop: 60, direction: 'rtl', padding: '28px 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ ...skm, width: '50%', height: 16 }} />
            <div style={{ ...skm, width: '90%', height: 12 }} />
            <div style={{ ...skm, width: '70%', height: 12 }} />
            <div style={{ ...skm, width: '80%', height: 12 }} />
          </div>
        ))}
      </div>
    </footer>
  );
}

/* ================= header ================= */
function collectDrawer(main) {
  const sections = [];
  (main || []).forEach(row => {
    (row.columns || []).forEach(col => {
      (col.widgets || []).filter(w => w.enabled !== false).forEach(w => {
        if ((w.type === 'menu' || w.type === 'mega_menu') && !(w.settings || {}).showOnMobile) sections.push(w);
      });
    });
  });
  return sections;
}

export function SiteHeader() {
  const { layout, vw, ready } = useSiteLayout();
  const mobile = vw < 768;
  const [cats, setCats] = useState([]);
  const [postCats, setPostCats] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [openAcc, setOpenAcc] = useState(null);

  useEffect(() => {
    const onToggle = () => setDrawer(o => { window.__siteDrawer = !o; return !o; });
    window.addEventListener('toggle-drawer', onToggle);
    return () => window.removeEventListener('toggle-drawer', onToggle);
  }, []);
  useEffect(() => {
    window.__siteDrawer = drawer;
    window.dispatchEvent(new Event('drawer-sync'));
  }, [drawer]);

  useEffect(() => {
    axios.get('/categories').then(r => setCats(Array.isArray(r.data) ? r.data : (r.data?.data || []))).catch(() => {});
    axios.get('/post-categories').then(r => setPostCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  if (!ready || !layout) return <HeaderSkeleton />;
  const platform = pickPlatform(layout.header, vw);
  if (!platform) return null;
  const hasContent = (platform.topbar || []).length > 0 || (platform.main || []).length > 0;
  if (!hasContent) return null;

  const ctx = { categories: cats, postCategories: postCats };
  const drawerWidgets = collectDrawer([...(platform.topbar || []), ...(platform.main || [])]);
  const mainBg = platform.main?.[0]?.settings?.bg || 'var(--card)';
  const visibleTopbar = headerSafeRows(mobile ? stripNavForMobile(platform.topbar) : platform.topbar);
  const visibleMain = headerSafeRows(mobile ? flattenMobileBar(stripNavForMobile(platform.main)) : platform.main);
  const hasDrawerBtn = [...(visibleTopbar || []), ...(visibleMain || [])].some(r =>
    (r.columns || []).some(c => (c.widgets || []).some(w => w.type === 'drawer_button' && w.enabled !== false)));
  const showOverlayBurger = mobile && drawerWidgets.length > 0 && !hasDrawerBtn;
  // Burger lives inside the topbar row (no extra bar) unless the topbar is empty too.
  const burgerInTop = mobile && drawerWidgets.length > 0 && !visibleMain.length && visibleTopbar.length > 0;
  const slimBar = mobile && !visibleMain.length && !visibleTopbar.length && drawerWidgets.length > 0;
  const burgerStyle = { background: 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 10, width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 };

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 100 }}>
      {(visibleTopbar.length > 0 || !mobile) && (
        <div style={{ position: 'relative', paddingLeft: burgerInTop ? 62 : undefined }}>
          <ChromeRows rows={visibleTopbar} ctx={ctx} />
          {burgerInTop && (
            <button onClick={() => setDrawer(o => !o)} aria-label="منو" style={{ ...burgerStyle, position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}>
              {drawer ? <X size={22} color="var(--primary)" /> : <MenuIcon size={22} color="var(--primary)" />}
            </button>
          )}
        </div>
      )}
      {slimBar ? (
        <div className="hb-chrome-bar" style={{ background: mainBg, borderBottom: '1px solid var(--border)', minHeight: 62, display: 'flex', alignItems: 'center', padding: '6px 12px' }}>
          <button onClick={() => setDrawer(o => !o)} aria-label="منو" style={burgerStyle}>
            {drawer ? <X size={22} color="var(--primary)" /> : <MenuIcon size={22} color="var(--primary)" />}
          </button>
        </div>
      ) : (
      (visibleMain.length > 0 || !mobile) && (
      <div className="hb-chrome-bar" style={{ background: mainBg, borderBottom: '1px solid var(--border)', position: 'relative', paddingLeft: showOverlayBurger ? 62 : undefined }}>
        <ChromeRows rows={visibleMain} ctx={ctx} />
        {showOverlayBurger && (
          <button onClick={() => setDrawer(o => !o)} aria-label="منو"
            style={{ ...burgerStyle, position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}>
            {drawer ? <X size={22} color="var(--primary)" /> : <MenuIcon size={22} color="var(--primary)" />}
          </button>
        )}
      </div>
      )
      )}
      {mobile && drawer && (
        <div style={{ background: 'var(--card)', borderBottom: '1px solid var(--border)', padding: '8px 16px 16px', maxHeight: '70vh', overflowY: 'auto', direction: 'rtl' }}>
          {drawerWidgets.map((w, wi) => {
            const s = w.settings || {};
            if (w.type === 'menu') return (
              <div key={wi}>
                {resolveMenuItems(s.items || [], ctx).map((it, i) => (
                  <div key={i}>
                    {(it.children && it.children.length) ? (
                      <>
                        <button onClick={() => setOpenAcc(openAcc === `${wi}-${i}` ? null : `${wi}-${i}`)}
                          style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'var(--fg)', fontSize: '0.95rem', fontWeight: 600, padding: '12px 6px' }}>
                          {it.label}<ChevronDown size={16} style={{ transform: openAcc === `${wi}-${i}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        {openAcc === `${wi}-${i}` && (
                          <div style={{ paddingRight: 12 }}>
                            {it.children.map((ch, j) => (
                              <SmartLink key={j} to={ch.url} onClick={() => setDrawer(false)} style={{ color: 'var(--muted-fg)', textDecoration: 'none', fontSize: '0.9rem', padding: '10px 6px', display: 'block' }}>{ch.label}</SmartLink>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <SmartLink to={it.url} onClick={() => setDrawer(false)} style={{ color: 'var(--fg)', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 600, padding: '12px 6px', display: 'block' }}>{it.label}</SmartLink>
                    )}
                  </div>
                ))}
              </div>
            );
            if (w.type === 'mega_menu') {
              const tree = s.mode === 'custom'
                ? (s.groups || []).map((g, gi) => ({ id: `g${gi}`, slug: null, name: g.title, children: (g.links || []).map((l, li) => ({ id: `g${gi}l${li}`, name: l.label, slug: null, url: l.url, children: [] })) }))
                : s.mode === 'post_categories'
                  ? (postCats || []).slice(0, s.limit || 8).map(c => ({ id: `pc${c.id}`, slug: null, name: `${c.name} (${Number(c.posts_count || 0).toLocaleString('fa-IR')})`, url: `/blog?category=${c.slug}`, children: [] }))
                  : buildTree(cats).slice(0, s.limit || 8);
              return (
                <div key={wi}>
                  <button onClick={() => setOpenAcc(openAcc === `mega-${wi}` ? null : `mega-${wi}`)}
                    style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'var(--fg)', fontSize: '0.95rem', fontWeight: 600, padding: '12px 6px' }}>
                    {s.title || 'دسته‌بندی‌ها'}<ChevronDown size={16} style={{ transform: openAcc === `mega-${wi}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>
                  {openAcc === `mega-${wi}` && (
                    <div style={{ paddingRight: 6 }}>
                      <CatTree nodes={tree} depth={0} onNavigate={() => setDrawer(false)}
                        getStyle={(d) => ({ color: d === 0 ? 'var(--card-fg, var(--fg))' : 'var(--muted-fg)', fontWeight: d === 0 ? 700 : 400, textDecoration: 'none', fontSize: d === 0 ? '0.92rem' : '0.87rem', padding: '9px 6px', display: 'block' })} />
                      <SmartLink to={s.mode === 'post_categories' ? '/blog' : '/products'} onClick={() => setDrawer(false)} style={{ color: 'var(--fg)', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 700, padding: '10px 6px', display: 'block' }}>{s.mode === 'post_categories' ? 'همه مطالب' : 'همه محصولات'}</SmartLink>
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </header>
  );
}

/* ================= footer ================= */
export function SiteFooter() {
  const { layout, vw, ready } = useSiteLayout();
  if (!ready || !layout) return <FooterSkeleton />;
  const rows = pickPlatform(layout.footer, vw);
  if (!rows || !rows.length) return null;
  return (
    <footer style={{ marginTop: 60, direction: 'rtl' }}>
      <ChromeRows rows={rows} ctx={{ categories: [] }} />
    </footer>
  );
}
